import { EMAIL_VALIDATION_REGEX } from 'src/utils/constants/email';
import { cpfValidator, dateValidator, fullNameValidator, phoneValidator } from 'src/utils/validators';
import cnpjValidator from 'src/utils/validators/cnpj-validator';
import { z } from 'zod';

const currentDate = new Date();
const minDate = new Date(currentDate.getFullYear() - 120, currentDate.getMonth(), currentDate.getDate());
const maxDate = new Date(currentDate.getFullYear() - 18, currentDate.getMonth(), currentDate.getDate());
const optionalStringField = z
  .string({
    error: (issue) => (issue.input === undefined ? undefined : 'O campo deve ser texto'),
  })
  .optional();

export const ADDRESS_FIELDS_AND_MESSAGES = [
  ['street', 'O logradouro é obrigatório'],
  ['number', 'O número é obrigatório'],
  ['neighborhood', 'O bairro é obrigatório'],
  ['cityId', 'A cidade é obrigatória'],
  ['stateId', 'A UF é obrigatória'],
];

export const CheckoutSchema = ({ isAddressRequired }: { isAddressRequired?: boolean }) =>
  z
    .object({
      // Personal data
      email: z
        .string({
          error: (issue) => (issue.input === undefined ? 'O e-mail é obrigatório' : 'O e-mail é obrigatório'),
        })
        .regex(EMAIL_VALIDATION_REGEX, {
          error: 'O e-mail está inválido',
        })
        .refine((val) => val.length > 0, {
          error: 'O e-mail é obrigatório',
        }),

      name: z
        .string({
          error: (issue) => (issue.input === undefined ? 'O nome é obrigatório' : 'O nome é obrigatório'),
        })
        .nonempty('O nome é obrigatório')
        .superRefine(
          fullNameValidator({
            messages: {
              required: 'O nome é obrigatório',
              fullNameRequired: 'O nome completo é obrigatório',
              onlyLettersAndAccentsIsAllowed: 'O nome deve conter apenas letras, acentos e espaços',
            },
          }),
        ),

      cpf: z
        .string({
          error: (issue) => (issue.input === undefined ? 'O CPF é obrigatório' : 'O CPF é obrigatório'),
        })
        .nonempty('O CPF é obrigatório')
        .superRefine(cpfValidator()),

      birthdate: z
        .string({
          error: (issue) =>
            issue.input === undefined ? 'A data de nascimento é obrigatória' : 'A data de nascimento é obrigatória',
        })
        .nonempty('A data de nascimento é obrigatória')
        .superRefine(
          dateValidator({
            minDate,
            maxDate,
            isFullDate: true,
            messages: {
              required: 'A data de nascimento é obrigatória',
              format: 'A data de nascimento está incompleta',
              invalid: 'A data de nascimento está inválida',
              min: 'A data de nascimento está inválida',
              max: 'É necessário ter mais de 18 anos',
            },
          }),
        ),

      phone: z
        .string({
          error: (issue) => (issue.input === undefined ? 'O celular é obrigatório' : 'O celular é obrigatório'),
        })
        .nonempty('O celular é obrigatório')
        .superRefine(phoneValidator()),

      // Address
      zipCode: optionalStringField,
      street: optionalStringField,
      number: optionalStringField,
      neighborhood: optionalStringField,
      cityId: optionalStringField,
      stateId: optionalStringField,
      complement: optionalStringField,

      // Terms of use

      accept: z
        .boolean({
          error: (issue) =>
            issue.input === undefined
              ? 'É necessário aceitar os termos de uso'
              : 'É necessário aceitar os termos de uso',
        })
        .refine((val) => val === true, {
          error: 'É necessário aceitar os termos de uso',
        }),

      customerType: z.enum(['cpf', 'cnpj']),
      customerCompanyDocument: z.string().optional(),
      customerCompanyName: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (!isAddressRequired) return;

      if (!data.zipCode || data.zipCode.trim().length < 9) {
        ctx.addIssue({ code: 'custom', message: 'O CEP é obrigatório', path: ['zipCode'] });
      }

      ADDRESS_FIELDS_AND_MESSAGES.forEach((item) => {
        if (!data[item[0]] || !data[item[0]].trim().length) {
          ctx.addIssue({ code: 'custom', message: item[1], path: [item[0]] });
        }
      });
    })
    .superRefine((data, ctx) => {
      if (data.customerType === 'cnpj') {
        cnpjValidator({ path: ['customerCompanyDocument'] })(data.customerCompanyDocument, ctx);

        if (!data.customerCompanyName || !data.customerCompanyName.trim()) {
          ctx.addIssue({ code: 'custom', message: 'A razão social é obrigatória', path: ['customerCompanyName'] });
        }
      }
    });

export type CheckoutSchemaFields = z.infer<ReturnType<typeof CheckoutSchema>>;
